/**
 * Write a pot off: a reason (one tap), an optional note, an optional photo as evidence.
 * Used from a pot's story in Reports and from the scan screen.
 */
import { useEffect, useRef, useState } from "react";
import { Camera, Trash2 } from "lucide-react";
import { WRITE_OFF_REASONS, type WriteOffReason } from "@hitl/rotations-domain";
import { Sheet } from "../scan/sheets";
import { WRITE_OFF_LABEL } from "../scan/copy";

export function WriteOffSheet(props: {
  potId: string;
  where?: string;
  /** Dev fixture only: focus the photo button so its focus ring can be reviewed. */
  focusPhoto?: boolean;
  onSubmit: (reason: WriteOffReason, note: string, photo: Blob | null) => void;
  onClose: () => void;
}) {
  const [reason, setReason] = useState<WriteOffReason | null>(null);
  const [note, setNote] = useState("");
  const [photo, setPhoto] = useState<Blob | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  const photoInput = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!props.focusPhoto) return;
    // After the sheet has focused itself.
    const t = setTimeout(() => photoInput.current?.focus(), 60);
    return () => clearTimeout(t);
  }, [props.focusPhoto]);

  useEffect(() => {
    if (!photo) return setPreview(null);
    const url = URL.createObjectURL(photo);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [photo]);

  return (
    <Sheet title={`Write off ${props.potId}?`} onClose={props.onClose}>
      <div className="sheet__body">
        <p className="sheet__lead">
          It leaves your stock for good{props.where ? ` (it's recorded at ${props.where})` : ""}. The record stays, dated, for your books.
        </p>
        <div className="choices" role="radiogroup" aria-label="Reason">
          {WRITE_OFF_REASONS.map((r) => (
            <button key={r} role="radio" aria-checked={reason === r} className={`choice${reason === r ? " is-picked" : ""}`} onClick={() => setReason(r)}>
              {WRITE_OFF_LABEL[r]}
            </button>
          ))}
        </div>
        <label className="field">
          <span className="field__label">Note (optional)</span>
          <input className="field__input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. enamel peeling on the rim" />
        </label>
        <label className="photo-pick">
          {preview ? <img src={preview} alt={`Photo of ${props.potId}`} /> : <Camera aria-hidden size={22} />}
          <span>{photo ? "Change photo" : "Add a photo (optional)"}</span>
          <input ref={photoInput} type="file" accept="image/*" capture="environment" onChange={(e) => setPhoto(e.target.files?.[0] ?? null)} />
        </label>
        <button className="btn btn--danger btn--block" disabled={!reason} onClick={() => reason && props.onSubmit(reason, note, photo)}>
          <Trash2 aria-hidden size={18} /> Write off <span className="inline-pot">{props.potId}</span>
        </button>
      </div>
    </Sheet>
  );
}
